// Generador de ZPL nativo para la etiqueta de bulto.
// ZPL es el lenguaje crudo de las impresoras térmicas (Zebra y compatibles): se le
// mandan comandos (^XA ... ^XZ) y la impresora dibuja texto y código de barras a nivel
// de punto. Calidad nativa, tamaño exacto, sin rasterizar.

// Tamaños de etiqueta soportados (en mm). El alto/ancho real en "dots" se calcula
// según el DPI de la impresora (203 u 300).
export const LABEL_SIZES = {
  "10x15": { label: "10 × 15 cm", widthMm: 100, heightMm: 150 },
  "10x10": { label: "10 × 10 cm", widthMm: 100, heightMm: 100 },
  "10x5": { label: "10 × 5 cm", widthMm: 100, heightMm: 50 },
};

export const DPI_OPTIONS = [
  { value: 203, label: "203 dpi (8 dots/mm)" },
  { value: 300, label: "300 dpi (12 dots/mm)" },
];

const mmToDots = (mm, dpi) => Math.round((mm / 25.4) * dpi);

// Sanitiza datos del usuario: ^ ~ \ son caracteres de control en ZPL y romperían la
// etiqueta. Además recorta strings largos para que no se desborden del ancho.
const z = (value, max) => {
  let v = String(value ?? "")
    .replace(/[\^~\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (max && v.length > max) v = v.slice(0, max - 1) + "…";
  return v;
};

const operativaLabel = (label) => {
  const m = String(label.shippingMethod || "").toLowerCase();
  if (m === "cliente") return "RETIRO / CLIENTE";
  if (m === "red_despacho") return "RED DE DESPACHO";
  if (m === "redespacho") return "REDESPACHO";
  if (m === "transporte_manual") return "TRANSPORTE";
  return String(label.shippingMethod || "").toUpperCase();
};

const transporteName = (label) => {
  const tc = label.thirdPartyCarrier || {};
  return tc.final?.companyName || tc.intermediate?.companyName || "";
};

// Construye el ZPL de UNA etiqueta de bulto. Coordenadas en mm → dots (escalan con DPI).
export const buildBultoZpl = (label, { dpi = 203, labelSize = "10x15" } = {}) => {
  const size = LABEL_SIZES[labelSize] || LABEL_SIZES["10x15"];
  const pw = mmToDots(size.widthMm, dpi); // print width (dots)
  const ll = mmToDots(size.heightMm, dpi); // label length (dots)
  const f = (mm) => mmToDots(mm, dpi); // helper mm → dots

  const d = label.destinatario || {};
  const barcodeVal = z(label.barcode || `${label.orderId || "ORD"}-B${label.index || 1}`, 40);
  const margin = f(3);
  const innerW = pw - margin * 2;

  const out = [];
  out.push("^XA");
  out.push("^CI28"); // UTF-8: respeta acentos y ñ
  out.push(`^PW${pw}`);
  out.push(`^LL${ll}`);
  out.push("^LH0,0");

  // Header negro con texto reverso
  const barH = f(13);
  out.push(`^FO0,0^GB${pw},${barH},${barH}^FS`);
  out.push(`^FR^FO${f(3)},${f(2.5)}^A0N,${f(7)},${f(7)}^FDBULTO ${label.index || 1}/${label.total || 1}^FS`);
  out.push(`^FR^FO${f(52)},${f(2.5)}^A0N,${f(3.2)},${f(3.2)}^FDOrden #${z(label.orderId, 16)}^FS`);
  out.push(`^FR^FO${f(52)},${f(7.5)}^A0N,${f(3.2)},${f(3.2)}^FD${z(operativaLabel(label), 22)}^FS`);

  // Destinatario
  out.push(`^FO${margin},${f(16)}^GB${innerW},2,2^FS`);
  out.push(`^FO${margin},${f(18)}^A0N,${f(2.6)},${f(2.6)}^FDDESTINATARIO^FS`);
  out.push(`^FO${margin},${f(22)}^A0N,${f(5)},${f(5)}^FD${z(d.nombre, 28)}^FS`);
  let y = 30;
  if (d.direccion) {
    out.push(`^FO${margin},${f(y)}^A0N,${f(3.2)},${f(3.2)}^FD${z(d.direccion, 40)}^FS`);
    y += 5;
  }
  const loc = [d.localidad, d.cp ? "CP " + d.cp : ""].filter(Boolean).join(" - ");
  if (loc) {
    out.push(`^FO${margin},${f(y)}^A0N,${f(3.2)},${f(3.2)}^FD${z(loc, 40)}^FS`);
    y += 5;
  }
  if (d.telefono) {
    out.push(`^FO${margin},${f(y)}^A0N,${f(3.2)},${f(3.2)}^FDTel: ${z(d.telefono, 24)}^FS`);
    y += 5;
  }
  if (d.dni) {
    out.push(`^FO${margin},${f(y)}^A0N,${f(3)},${f(3)}^FDDNI/CUIT: ${z(d.dni, 18)}^FS`);
    y += 5;
  }

  // Producto
  out.push(`^FO${margin},${f(62)}^A0N,${f(2.6)},${f(2.6)}^FDPRODUCTO^FS`);
  out.push(`^FO${margin},${f(66)}^A0N,${f(3.4)},${f(3.4)}^FB${innerW},2,0,L^FD${z(label.displayName, 80)}^FS`);

  // Transporte (si aplica)
  const tn = transporteName(label);
  if (tn) {
    out.push(`^FO${margin},${f(80)}^A0N,${f(3)},${f(3)}^FDTransporte: ${z(tn, 30)}^FS`);
  }

  // Código de barras nativo (Code128)
  out.push(`^BY3,2,${f(20)}`);
  out.push(`^FO${f(12)},${f(88)}^BCN,${f(20)},Y,N,N^FD${barcodeVal}^FS`);

  // Remitente
  out.push(`^FO${margin},${f(125)}^GB${innerW},2,2^FS`);
  out.push(`^FO${margin},${f(128)}^A0N,${f(2.4)},${f(2.4)}^FDREMITENTE^FS`);
  out.push(`^FO${margin},${f(132)}^A0N,${f(2.6)},${f(2.6)}^FB${innerW},3,0,L^FD${z(label.remitente, 90)}^FS`);

  out.push("^XZ");
  return out.join("");
};

// ZPL de prueba para el botón "Probar impresión".
export const buildTestZpl = ({ dpi = 203, labelSize = "10x15" } = {}) => {
  const size = LABEL_SIZES[labelSize] || LABEL_SIZES["10x15"];
  const pw = mmToDots(size.widthMm, dpi);
  const ll = mmToDots(size.heightMm, dpi);
  const f = (mm) => mmToDots(mm, dpi);
  return [
    "^XA",
    "^CI28",
    `^PW${pw}`,
    `^LL${ll}`,
    "^LH0,0",
    `^FO${f(3)},${f(3)}^GB${pw - f(6)},${f(13)},3^FS`,
    `^FO${f(8)},${f(6)}^A0N,${f(6)},${f(6)}^FDFICANAZ WMS^FS`,
    `^FO${f(8)},${f(20)}^A0N,${f(4)},${f(4)}^FDPrueba de impresion OK^FS`,
    `^FO${f(8)},${f(28)}^A0N,${f(3)},${f(3)}^FD${size.label} - ${dpi} dpi^FS`,
    `^BY3,2,${f(18)}`,
    `^FO${f(12)},${f(38)}^BCN,${f(18)},Y,N,N^FDFICANAZ-TEST^FS`,
    "^XZ",
  ].join("");
};

// ─────────────────────────────────────────────────────────────────────────────
// TSPL (lenguaje nativo de las Xprinter / TSC). Las impresoras suelen hablar UNO
// de los dos lenguajes; el selector del perfil decide cuál usar.
// ─────────────────────────────────────────────────────────────────────────────

export const PRINT_LANGUAGES = [
  { value: "zpl", label: "ZPL (Zebra y compatibles)" },
  { value: "tspl", label: "TSPL (Xprinter / TSC)" },
];

// Quita acentos: las fuentes bitmap de TSPL no renderizan bien UTF-8, así que
// normalizamos (á→a, ñ→n) para que el texto salga legible.
const stripCombiningMarks = (str) => {
  let out = "";
  for (const ch of str) {
    const code = ch.codePointAt(0);
    if (code >= 0x300 && code <= 0x36f) continue; // marcas diacríticas combinantes
    out += ch;
  }
  return out;
};

const deburr = (s) => stripCombiningMarks(String(s ?? "").normalize("NFD"));

// Sanitiza para TSPL: las cadenas van entre comillas dobles, así que sacamos " y \
// y los saltos de línea, y recortamos largos.
const t = (value, max) => {
  let v = deburr(value)
    .replace(/["\\\r\n]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (max && v.length > max) v = v.slice(0, max - 3) + "...";
  return v;
};

// Construye el TSPL de UNA etiqueta de bulto. Coordenadas de texto/barcode en DOTS;
// SIZE/GAP en mm. Texto blanco sobre negro = imprimir negro y luego REVERSE la zona.
export const buildBultoTspl = (label, { dpi = 203, labelSize = "10x15" } = {}) => {
  const size = LABEL_SIZES[labelSize] || LABEL_SIZES["10x15"];
  const f = (mm) => mmToDots(mm, dpi);
  const pw = f(size.widthMm);
  const innerW = pw - f(6);
  const d = label.destinatario || {};
  const barcodeVal = t(label.barcode || `${label.orderId || "ORD"}-B${label.index || 1}`, 40);

  const out = [];
  out.push(`SIZE ${size.widthMm} mm, ${size.heightMm} mm`);
  out.push("GAP 3 mm, 0 mm");
  out.push("DIRECTION 1");
  out.push("CLS");

  // Header (texto negro → REVERSE para que quede blanco sobre negro)
  out.push(`TEXT ${f(3)},${f(3)},"4",0,1,1,"BULTO ${label.index || 1}/${label.total || 1}"`);
  out.push(`TEXT ${f(52)},${f(3)},"2",0,1,1,"Orden #${t(label.orderId, 16)}"`);
  out.push(`TEXT ${f(52)},${f(8)},"2",0,1,1,"${t(operativaLabel(label), 22)}"`);
  out.push(`REVERSE 0,0,${pw},${f(13)}`);

  // Destinatario
  out.push(`TEXT ${f(3)},${f(18)},"2",0,1,1,"DESTINATARIO"`);
  out.push(`TEXT ${f(3)},${f(23)},"4",0,1,1,"${t(d.nombre, 28)}"`);
  let y = 31;
  if (d.direccion) {
    out.push(`TEXT ${f(3)},${f(y)},"3",0,1,1,"${t(d.direccion, 38)}"`);
    y += 6;
  }
  const loc = [d.localidad, d.cp ? "CP " + d.cp : ""].filter(Boolean).join(" - ");
  if (loc) {
    out.push(`TEXT ${f(3)},${f(y)},"3",0,1,1,"${t(loc, 38)}"`);
    y += 6;
  }
  if (d.telefono) {
    out.push(`TEXT ${f(3)},${f(y)},"3",0,1,1,"Tel: ${t(d.telefono, 24)}"`);
    y += 6;
  }
  if (d.dni) {
    out.push(`TEXT ${f(3)},${f(y)},"3",0,1,1,"DNI/CUIT: ${t(d.dni, 18)}"`);
    y += 6;
  }

  // Producto (BLOCK = wrap automático)
  out.push(`TEXT ${f(3)},${f(64)},"2",0,1,1,"PRODUCTO"`);
  out.push(`BLOCK ${f(3)},${f(69)},${innerW},${f(16)},"3",0,1,1,"${t(label.displayName, 80)}"`);

  // Transporte
  const tn = transporteName(label);
  if (tn) out.push(`TEXT ${f(3)},${f(86)},"3",0,1,1,"Transporte: ${t(tn, 30)}"`);

  // Código de barras nativo (Code128)
  out.push(`BARCODE ${f(12)},${f(95)},"128",${f(20)},1,0,3,6,"${barcodeVal}"`);

  // Remitente
  out.push(`TEXT ${f(3)},${f(128)},"2",0,1,1,"REMITENTE"`);
  out.push(`BLOCK ${f(3)},${f(133)},${innerW},${f(14)},"2",0,1,1,"${t(label.remitente, 90)}"`);

  out.push("PRINT 1,1");
  return out.join("\r\n") + "\r\n";
};

export const buildTestTspl = ({ dpi = 203, labelSize = "10x15" } = {}) => {
  const size = LABEL_SIZES[labelSize] || LABEL_SIZES["10x15"];
  const f = (mm) => mmToDots(mm, dpi);
  return (
    [
      `SIZE ${size.widthMm} mm, ${size.heightMm} mm`,
      "GAP 3 mm, 0 mm",
      "DIRECTION 1",
      "CLS",
      `TEXT ${f(5)},${f(5)},"5",0,1,1,"FICANAZ WMS"`,
      `TEXT ${f(5)},${f(17)},"3",0,1,1,"Prueba TSPL OK"`,
      `TEXT ${f(5)},${f(25)},"2",0,1,1,"${size.label} - ${dpi} dpi"`,
      `BARCODE ${f(12)},${f(35)},"128",${f(18)},1,0,3,6,"FICANAZ-TEST"`,
      "PRINT 1,1",
    ].join("\r\n") + "\r\n"
  );
};

// Dispatchers: eligen el lenguaje según la config de la impresora.
export const buildBultoLabel = (label, { language = "zpl", dpi = 203, labelSize = "10x15" } = {}) =>
  language === "tspl"
    ? buildBultoTspl(label, { dpi, labelSize })
    : buildBultoZpl(label, { dpi, labelSize });

export const buildTestLabel = ({ language = "zpl", dpi = 203, labelSize = "10x15" } = {}) =>
  language === "tspl" ? buildTestTspl({ dpi, labelSize }) : buildTestZpl({ dpi, labelSize });
